/**
 * An `<img>` that degrades to a tasteful placeholder instead of the browser's
 * broken-image glyph when its source fails to load — an expired CDN URL, a
 * network blip, or the offline mock all land here.
 *
 * Behaviour-preserving: while the image loads and once it succeeds this renders
 * exactly what a plain `<img>` would; the placeholder only ever appears AFTER an
 * `error` event (or when no `src` is supplied). That makes it a safe drop-in on
 * the shared approver surfaces real reviewers see — a broken CDN image is just
 * as ugly for them — not only on the demo.
 *
 * `variant` picks the placeholder:
 *   - `media`  → a neutral tile with a Font Awesome image glyph, matching the
 *                existing "no thumbnail" tiles in MarketerMirror / DemoFlyLayer.
 *   - `avatar` → the subject's initial (from `fallbackLabel`), or a user glyph.
 */
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { useEffect, useState } from 'react'

export interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string
  variant?: 'media' | 'avatar'
  /** Avatar variant: its first letter shows when the image fails to load. */
  fallbackLabel?: string
  /**
   * Class for the fallback tile. Defaults to `className` so the placeholder
   * fills exactly the box the image would have. Set it when the `<img>` itself
   * is unclassed and positioned by a parent selector (e.g. `[&>img]`).
   */
  fallbackClassName?: string
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  variant = 'media',
  fallbackLabel,
  fallbackClassName,
  className,
  style,
  alt = '',
  onError,
  ...rest
}) => {
  const [failed, setFailed] = useState(false)

  // A changed `src` is a fresh chance to load — clear the error so the new
  // image renders instead of being pinned to the previous failure's tile.
  useEffect(() => {
    setFailed(false)
  }, [src])

  // Gate only on a real load error, not on `src` being falsy: a plain <img>
  // with no src is what these call sites rendered before, so keeping that keeps
  // the successful path byte-identical. A missing/empty src that the browser
  // rejects still fires `error`, which swaps in the placeholder just the same.
  if (!failed) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onError={(event) => {
          setFailed(true)
          onError?.(event)
        }}
        {...rest}
      />
    )
  }

  const initial = fallbackLabel?.trim().slice(0, 1).toUpperCase()

  return (
    <span
      aria-hidden="true"
      data-testid="image-fallback"
      style={style}
      className={cn(
        'flex items-center justify-center',
        variant === 'avatar'
          ? 'bg-neutral-100 text-xs font-semibold text-neutral-500'
          : 'bg-neutral-50 text-neutral-300',
        fallbackClassName ?? className
      )}
    >
      {variant === 'avatar' && initial ? (
        initial
      ) : (
        <i
          className={
            variant === 'avatar' ? 'fa-solid fa-user' : 'fa-regular fa-image'
          }
        />
      )}
    </span>
  )
}

export default ImageWithFallback
